import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Paper,
  Popover,
  Popper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import QuickreplyOutlinedIcon from '@mui/icons-material/QuickreplyOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { AntChatSavedReply } from '../../hooks/chat/api';
import { replyScopeLabel, searchSavedReplies } from './savedReplies';

/** At most this many matches under the "/" shortcut. */
const SLASH_LIMIT = 8;

interface ListProps {
  replies: AntChatSavedReply[];
  activeIndex: number;
  loading: boolean;
  error: string | null;
  emptyText: string;
  onPick: (reply: AntChatSavedReply) => void;
  onHover?: (index: number) => void;
}

/** The matches, title over a one-line preview of the body. */
export function SavedReplyList({
  replies,
  activeIndex,
  loading,
  error,
  emptyText,
  onPick,
  onHover,
}: ListProps) {
  if (loading && replies.length === 0) {
    return (
      <Stack alignItems="center" sx={{ py: 2 }}>
        <CircularProgress size={18} />
      </Stack>
    );
  }
  if (error && replies.length === 0) {
    return (
      <Typography variant="body2" color="error" sx={{ px: 2, py: 1.5 }}>
        Could not load saved replies. {error}
      </Typography>
    );
  }
  if (replies.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
        {emptyText}
      </Typography>
    );
  }
  return (
    <List dense disablePadding role="listbox" aria-label="Saved replies">
      {replies.map((reply, index) => (
        <ListItemButton
          key={reply.id}
          selected={index === activeIndex}
          role="option"
          aria-selected={index === activeIndex}
          // Keep the composer's focus: a click must not blur the text field first.
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={onHover ? () => onHover(index) : undefined}
          onClick={() => onPick(reply)}
          sx={{ display: 'block', px: 2, py: 0.75 }}
          data-testid="saved-reply-option"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }} noWrap>
              {reply.title}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={replyScopeLabel(reply)}
              sx={{ height: 20, fontSize: 11 }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {reply.body}
          </Typography>
        </ListItemButton>
      ))}
    </List>
  );
}

/** Keyboard moves through a list of `count` items, wrapping around. */
export function moveIndex(current: number, delta: number, count: number): number {
  if (count === 0) return 0;
  return (current + delta + count) % count;
}

interface SlashProps {
  /** What follows the "/"; null hides the list. */
  query: string | null;
  anchorEl: HTMLElement | null;
  replies: AntChatSavedReply[] | null;
  loading: boolean;
  error: string | null;
  activeIndex: number;
  onHover: (index: number) => void;
  onPick: (reply: AntChatSavedReply) => void;
}

/** The matches for what follows "/" at the start of the composer. */
export function slashMatches(
  replies: AntChatSavedReply[] | null,
  query: string | null
): AntChatSavedReply[] {
  if (query === null || !replies) return [];
  return searchSavedReplies(replies, query).slice(0, SLASH_LIMIT);
}

/** The "/" shortcut's list, over the composer. The composer owns the keys. */
export function SlashReplyMenu({
  query,
  anchorEl,
  replies,
  loading,
  error,
  activeIndex,
  onHover,
  onPick,
}: SlashProps) {
  const matches = useMemo(() => slashMatches(replies, query), [replies, query]);
  const open = query !== null && Boolean(anchorEl);
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top-start"
      style={{ zIndex: 1300, width: anchorEl?.clientWidth }}
      data-testid="slash-replies"
    >
      <Paper elevation={8} sx={{ maxHeight: 320, overflowY: 'auto', mb: 0.5 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: 'block', px: 2, pt: 1, lineHeight: 1.6 }}
        >
          Saved replies · ↑↓ to pick, Enter to insert, Esc to close
        </Typography>
        <SavedReplyList
          replies={matches}
          activeIndex={activeIndex}
          loading={loading}
          error={error}
          emptyText={
            replies && replies.length === 0
              ? 'No saved replies yet. Add some with Manage.'
              : 'No saved reply matches'
          }
          onPick={onPick}
          onHover={onHover}
        />
      </Paper>
    </Popper>
  );
}

interface ButtonProps {
  replies: AntChatSavedReply[] | null;
  loading: boolean;
  error: string | null;
  disabled?: boolean;
  /** Called when the list is opened: the first open reads the replies. */
  onOpen: () => void;
  onPick: (reply: AntChatSavedReply) => void;
  onManage: () => void;
}

/** The composer's saved-reply button: a searchable list and a way to manage them. */
export function SavedReplyButton({
  replies,
  loading,
  error,
  disabled,
  onOpen,
  onPick,
  onManage,
}: ButtonProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(0);
  const matches = useMemo(
    () => (replies ? searchSavedReplies(replies, search) : []),
    [replies, search]
  );

  useEffect(() => setActive(0), [search, anchor]);

  const close = () => {
    setAnchor(null);
    setSearch('');
  };
  const pick = (reply: AntChatSavedReply) => {
    close();
    onPick(reply);
  };

  return (
    <>
      <Tooltip title="Saved replies (or type / at the start)">
        <span>
          <IconButton
            aria-label="Saved replies"
            disabled={disabled}
            onClick={(e) => {
              setAnchor(e.currentTarget);
              onOpen();
            }}
          >
            <QuickreplyOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { width: 380, maxWidth: 'calc(100vw - 32px)' } }}
      >
        <Box sx={{ p: 1.5, pb: 1 }}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="Search saved replies"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((i) =>
                  moveIndex(i, e.key === 'ArrowDown' ? 1 : -1, matches.length)
                );
              } else if (e.key === 'Enter' && matches[active]) {
                e.preventDefault();
                pick(matches[active]);
              }
            }}
            inputProps={{ 'aria-label': 'Search saved replies' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
          <SavedReplyList
            replies={matches}
            activeIndex={active}
            loading={loading}
            error={error}
            emptyText={
              replies && replies.length === 0
                ? 'No saved replies yet.'
                : 'No saved reply matches'
            }
            onPick={pick}
            onHover={setActive}
          />
        </Box>
        <Box sx={{ borderTop: 1, borderColor: 'divider', px: 1, py: 0.5 }}>
          <Button
            size="small"
            startIcon={<SettingsOutlinedIcon fontSize="small" />}
            onClick={() => {
              close();
              onManage();
            }}
          >
            Manage saved replies
          </Button>
        </Box>
      </Popover>
    </>
  );
}
